import { useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6rim6lsbgrc47fimk80', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-navy-900">
        <div className="absolute inset-0 opacity-10">
          <div
            className="w-full h-full"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C9A961' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          ></div>
        </div>
        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block px-6 py-2 border border-gold-500 rounded-full mb-6">
              <span className="text-xs font-sans uppercase tracking-widest text-gold-500">İLETİŞİM</span>
            </div>
            <h1 className="font-serif text-5xl lg:text-6xl text-white mb-6">
              Bizimle İletişime<br />Geçin
            </h1>
            <p className="font-sans text-lg text-white/80 max-w-3xl mx-auto leading-relaxed">
              Hukuki danışmanlık için bizimle iletişime geçin. Deneyimli avukatlarımız size yardımcı olmaya hazır.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <motion.div {...fadeInUp} className="lg:col-span-1">
              <h2 className="font-serif text-3xl text-navy-900 mb-8">İletişim Bilgileri</h2>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-gold-500/10 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-gold-500"></i>
                  </div>
                  <div>
                    <h3 className="font-sans text-base font-semibold text-navy-900 mb-1">Ofis Adresimiz</h3>
                    <p className="font-sans text-sm text-gray-600 leading-relaxed">
                      Levent Mahallesi, Büyükdere Caddesi<br />
                      No: 123, Kat: 5<br />
                      34330 Beşiktaş, İstanbul
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-gold-500/10 rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-xl text-gold-500"></i>
                  </div>
                  <div>
                    <h3 className="font-sans text-base font-semibold text-navy-900 mb-1">Telefon</h3>
                    <p className="font-sans text-sm text-gray-600">0216 479 60 29</p>
                    <p className="font-sans text-sm text-gray-600">0533 623 82 34</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-gold-500/10 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-xl text-gold-500"></i>
                  </div>
                  <div>
                    <h3 className="font-sans text-base font-semibold text-navy-900 mb-1">E-posta</h3>
                    <p className="font-sans text-sm text-gray-600">info@fphukuk.com</p>
                    <p className="font-sans text-sm text-gray-600">danismanlik@fphukuk.com</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-gold-500/10 rounded-lg flex-shrink-0">
                    <i className="ri-time-line text-xl text-gold-500"></i>
                  </div>
                  <div>
                    <h3 className="font-sans text-base font-semibold text-navy-900 mb-1">Çalışma Saatleri</h3>
                    <p className="font-sans text-sm text-gray-600">Pazartesi - Cuma: 09:00 - 18:00</p>
                    <p className="font-sans text-sm text-gray-600">Cumartesi: 10:00 - 14:00</p>
                    <p className="font-sans text-sm text-gray-600">Pazar: Kapalı</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="font-sans text-base font-semibold text-navy-900 mb-4">Bizi Takip Edin</h3>
                <div className="flex items-center space-x-3">
                  <a
                    href="https://linkedin.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-md hover:border-gold-500 hover:text-gold-500 transition-colors cursor-pointer"
                    aria-label="LinkedIn"
                  >
                    <i className="ri-linkedin-line text-lg"></i>
                  </a>
                  <a
                    href="https://twitter.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-md hover:border-gold-500 hover:text-gold-500 transition-colors cursor-pointer"
                    aria-label="Twitter"
                  >
                    <i className="ri-twitter-x-line text-lg"></i>
                  </a>
                  <a
                    href="https://instagram.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-md hover:border-gold-500 hover:text-gold-500 transition-colors cursor-pointer"
                    aria-label="Instagram"
                  >
                    <i className="ri-instagram-line text-lg"></i>
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div {...fadeInUp} className="lg:col-span-2">
              <div className="bg-gray-50 p-8 lg:p-12 rounded-lg">
                <h2 className="font-serif text-3xl text-navy-900 mb-2">Danışmanlık Talep Edin</h2>
                <p className="font-sans text-base text-gray-600 mb-8">
                  Formu doldurun, en kısa sürede size geri dönelim
                </p>

                <form id="consultation-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block font-sans text-sm font-medium text-navy-900 mb-2">
                        Ad Soyad *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white border border-gray-300 rounded-md font-sans text-sm text-navy-900 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                        placeholder="Adınız ve soyadınız"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block font-sans text-sm font-medium text-navy-900 mb-2">
                        E-posta *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white border border-gray-300 rounded-md font-sans text-sm text-navy-900 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                        placeholder="ornek@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block font-sans text-sm font-medium text-navy-900 mb-2">
                        Telefon *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white border border-gray-300 rounded-md font-sans text-sm text-navy-900 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                        placeholder="+90 (5XX) XXX XX XX"
                      />
                    </div>

                    <div>
                      <label htmlFor="subject" className="block font-sans text-sm font-medium text-navy-900 mb-2">
                        Konu *
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white border border-gray-300 rounded-md font-sans text-sm text-navy-900 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent cursor-pointer"
                      >
                        <option value="">Konu seçiniz</option>
                        <option value="Kurumsal Hukuk">Kurumsal Hukuk</option>
                        <option value="Ticari Dava">Ticari Dava</option>
                        <option value="Sözleşme Hukuku">Sözleşme Hukuku</option>
                        <option value="Gayrimenkul Hukuku">Gayrimenkul Hukuku</option>
                        <option value="İnşaat Hukuku">İnşaat Hukuku</option>
                        <option value="İcra ve İflas Hukuku">İcra ve İflas Hukuku</option>
                        <option value="Uluslararası Ticaret Hukuku">Uluslararası Ticaret Hukuku</option>
                        <option value="Yatırım Hukuku">Yatırım Hukuku</option>
                        <option value="Aile Hukuku">Aile Hukuku</option>
                        <option value="Ceza Hukuku">Ceza Hukuku</option>
                        <option value="Diğer">Diğer</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block font-sans text-sm font-medium text-navy-900 mb-2">
                      Mesajınız *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      maxLength={500}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-md font-sans text-sm text-navy-900 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent resize-none"
                      placeholder="Hukuki danışmanlık ihtiyacınızı detaylı olarak açıklayınız (maksimum 500 karakter)"
                    ></textarea>
                    <p className="mt-1 text-xs text-gray-500 text-right">{formData.message.length}/500</p>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                      <p className="font-sans text-sm text-green-800">
                        Mesajınız başarıyla gönderildi. En kısa sürede size geri dönüş yapacağız.
                      </p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                      <p className="font-sans text-sm text-red-800">
                        Bir hata oluştu. Lütfen daha sonra tekrar deneyiniz.
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-4 bg-gold-500 text-white font-sans text-base font-medium rounded-md hover:bg-gold-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? 'Gönderiliyor...' : 'Mesaj Gönder'}
                  </button>

                  <p className="font-sans text-xs text-gray-500 text-center">
                    Formu göndererek <a href="#" className="text-gold-500 hover:text-gold-600 cursor-pointer">Gizlilik Politikamızı</a> kabul etmiş olursunuz.
                  </p>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-0 bg-white">
        <div className="w-full h-[500px]">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3008.3964738876!2d29.011489315415!3d41.08037797929!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cab7650656bd63%3A0x8ca058b28c20b6c3!2zTGV2ZW50LCBCZcWfaWt0YcWfL8Swc3RhbmJ1bA!5e0!3m2!1sen!2str!4v1234567890123!5m2!1sen!2str"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Office Location"
          ></iframe>
        </div>
      </section>

      {/* Legal Notice */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <motion.div {...fadeInUp} className="bg-white p-8 rounded-lg border-l-4 border-gold-500">
            <h3 className="font-sans text-lg font-semibold text-navy-900 mb-4">
              <i className="ri-information-line text-gold-500 mr-2"></i>
              Önemli Bilgilendirme
            </h3>
            <div className="font-sans text-sm text-gray-700 leading-relaxed space-y-2">
              <p>
                <strong>Müvekkil Gizliliği:</strong> Tüm görüşmeler ve paylaşılan bilgiler avukatlık meslek sırrı kapsamında korunmaktadır.
              </p>
              <p>
                <strong>İlk Görüşme:</strong> İlk danışmanlık görüşmesi ücretsizdir ve herhangi bir yükümlülük gerektirmez.
              </p>
              <p>
                <strong>Yanıt Süresi:</strong> Taleplerinize en geç 24 saat içinde yanıt verilmektedir.
              </p>
              <p>
                <strong>Acil Durumlar:</strong> Acil hukuki destek için lütfen telefon ile iletişime geçiniz.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;